import moment from 'moment'
import { isNil, isNull } from 'lodash-es'
import { HaircutType, NegotiableFieldOptional, NegotiableFieldRequired, Negotiation, NegotiationDetail, OrderTerm, RfqType } from 'api'
import { DateLike, Decimal, primitive } from 'utils'
import { Locale, Translation } from 'locale'
import { getNegotiatedValue, isPricedByBothEntities } from './utils'
import { isNotNil } from 'utils/predicate'

export enum ModificationType {
  INTERNAL = 'INTERNAL',
  INITIATOR = 'INITIATOR',
  RESPONDER = 'RESPONDER'
}

export type CompareField = keyof Omit<NegotiableFieldRequired<any>, 'modified'>

function getModificationType<T>(
  field: NegotiableFieldOptional<T> | null | undefined,
  getDelta: (field: NegotiableFieldOptional<T> | null | undefined, compareField?: CompareField) => primitive | null
) {
  const internalChange = !!getDelta(field, 'negotiated')
  // If initiator changed the value, then responderLastValue should be different to negotiated/initiatorLastValue, and vice versa
  const initiatorChange = !!getDelta(field, 'responderLastValue')
  const responderChange = !!getDelta(field, 'initiatorLastValue')

  if (internalChange) {
    return ModificationType.INTERNAL
  } else if (initiatorChange) {
    return ModificationType.INITIATOR
  } else if (responderChange) {
    return ModificationType.RESPONDER
  } else {
    return null
  }
}

/* Strings */

function getStringDelta(field: NegotiableFieldOptional<string> | null | undefined, compareField: CompareField = 'original') {
  const negotiationValue = field && getNegotiatedValue(field)
  const compareValue = field && field[compareField]
  return negotiationValue !== compareValue
}

export function getStringModificationType(field: NegotiableFieldOptional<string> | null | undefined) {
  return getModificationType(field, getStringDelta)
}

/* Numerics */

function getNumericDelta(originalValue: number | null | undefined, negotiationValue: number | null | undefined) {
  if (originalValue === negotiationValue) {
    return null
  } else {
    return Decimal.subtract(negotiationValue || 0, originalValue || 0)
  }
}

export function getHaircutDelta(field: NegotiableFieldOptional<number> | null | undefined, compareField: CompareField = 'original') {
  const negotiationValue = field && getNegotiatedValue(field)
  const originalValue = field && field[compareField]
  return getNumericDelta(originalValue, negotiationValue)
}

export function getHaircutModificationType(field: NegotiableFieldOptional<number> | null | undefined) {
  return isPricedByBothEntities(field) ? getModificationType(field, getHaircutDelta) : null
}

export function getRateDelta(field: NegotiableFieldOptional<number> | null | undefined, compareField: CompareField = 'original') {
  const negotiationValue = field && getNegotiatedValue(field)
  const originalValue = field && field[compareField]
  return getNumericDelta(originalValue, negotiationValue)
}

export function getRateModificationType(field: NegotiableFieldOptional<number> | null | undefined) {
  return isPricedByBothEntities(field) ? getModificationType(field, getRateDelta) : null
}

export function getIsFirstEnteredHaircutAndHaircutType(negotiationDetail: { negotiation: Negotiation } | null): boolean {
  if (!negotiationDetail) {
    return false
  }
  return isFirstEnteredHaircut(negotiationDetail) && isFirstEnteredHaircutType(negotiationDetail)
}

export function isFirstEnteredRate(negotiationDetail: { negotiation: Negotiation } | null): boolean {
  if (!negotiationDetail) {
    return false
  }

  const negotiation = negotiationDetail.negotiation

  if (negotiation.version === '1.0') {
    const hasNegotiatedValue = getNegotiatedValue(negotiation.rate)
    return !!hasNegotiatedValue
  }

  return firstValueByResponder(negotiation.rate)
}

function isFirstEnteredHaircut(negotiationDetail: { negotiation: Negotiation }): boolean {
  const negotiation = negotiationDetail.negotiation

  return firstValueByResponder(negotiation.haircut) || firstValueBySystem(negotiation.haircut)
}

function isFirstEnteredHaircutType(negotiationDetail: { negotiation: Negotiation }): boolean {
  const negotiation = negotiationDetail.negotiation

  return firstValueByResponder(negotiation.haircutType) || firstValueBySystem(negotiation.haircutType)
}

function firstValueByResponder(negotiableField: NegotiableFieldOptional<any>): boolean {
  const initiatorValueIsNull = isNull(negotiableField.initiatorLastValue)
  const responderLastValueIsNull = isNull(negotiableField.responderLastValue)
  return initiatorValueIsNull && !responderLastValueIsNull
}

function firstValueBySystem(negotiableField: NegotiableFieldOptional<any>): boolean {
  return (
    negotiableField.modified &&
    isNull(negotiableField.initiatorLastValue) &&
    isNull(negotiableField.responderLastValue) &&
    isNull(negotiableField.negotiated) &&
    isNull(negotiableField.original) &&
    isNotNil(negotiableField.modifiedValue)
  )
}

export function getNotionalDelta(field: NegotiableFieldOptional<number> | null | undefined, compareField: CompareField = 'original') {
  const negotiationValue = field && getNegotiatedValue(field)
  const originalValue = field && field[compareField]
  return getNumericDelta(originalValue, negotiationValue)
}

export function getNotionalModificationType(field: NegotiableFieldOptional<number> | null | undefined) {
  return getModificationType(field, getNotionalDelta)
}

export function getStartConsiderationDelta(
  field: NegotiableFieldOptional<number> | null | undefined,
  compareField: CompareField = 'original'
) {
  const negotiationValue = field && getNegotiatedValue(field)
  const originalValue = field && field[compareField]
  return getNumericDelta(originalValue, negotiationValue)
}

export function getStartConsiderationModificationType(field: NegotiableFieldOptional<number> | null | undefined) {
  return getModificationType(field, getStartConsiderationDelta)
}

export function getStartConsiderationOverrideDelta(
  field: NegotiableFieldRequired<boolean> | null | undefined,
  compareField: CompareField = 'original'
) {
  const negotiationValue = field && getNegotiatedValue(field)
  const originalValue = field && field[compareField]
  return negotiationValue !== originalValue
}

export function getStartConsiderationOverrideModificationType(field: NegotiableFieldRequired<boolean> | null | undefined) {
  return getModificationType(field, getStartConsiderationOverrideDelta)
}

export function getBondPriceDelta(field: NegotiableFieldOptional<number> | null | undefined, compareField: CompareField = 'original') {
  const negotiationValue = field && getNegotiatedValue(field)
  const originalValue = field && field[compareField]
  return getNumericDelta(originalValue, negotiationValue)
}

export function getBondPriceModificationType(field: NegotiableFieldOptional<number> | null | undefined) {
  return getModificationType(field, getBondPriceDelta)
}

/* Dates */

function getDateDelta(originalValue: DateLike | null | undefined, negotiationValue: DateLike | null | undefined) {
  if (isNil(originalValue) || isNil(negotiationValue) || negotiationValue === originalValue) {
    return null
  } else {
    const delta = moment(negotiationValue).diff(moment(originalValue))

    if (delta === 0) {
      return null
    } else {
      const deltaDays = moment.duration(delta, 'ms').asDays()

      if (deltaDays === 0) {
        return null
      } else {
        return Locale.translate(Translation.SC_DELTA_DAYS, {
          days: Math.abs(deltaDays),
          formattedDays: Locale.formatNumber(deltaDays, { sign: true })
        })
      }
    }
  }
}

export function getStartDateDelta(field: NegotiableFieldOptional<DateLike> | null | undefined, compareField: CompareField = 'original') {
  const negotiationValue = field && getNegotiatedValue(field)
  const originalValue = field && field[compareField]
  return getDateDelta(originalValue, negotiationValue)
}

export function getStartDateModificationType(field: NegotiableFieldOptional<DateLike> | null | undefined) {
  return getModificationType(field, getStartDateDelta)
}

export function getEndDateDelta(field: NegotiableFieldOptional<DateLike> | null | undefined, compareField: CompareField = 'original') {
  const negotiationValue = field && getNegotiatedValue(field)
  const originalValue = field && field[compareField]
  return getDateDelta(originalValue, negotiationValue)
}

export function getEndDateModificationType(field: NegotiableFieldOptional<DateLike> | null | undefined) {
  return getModificationType(field, getEndDateDelta)
}

export function getEnumDelta<T>(field: NegotiableFieldOptional<T> | null | undefined, compareField: CompareField = 'original') {
  const negotiationValue = field && getNegotiatedValue(field)
  const originalValue = field && field[compareField]
  return negotiationValue !== originalValue
}

export function getTermModificationType(field: NegotiableFieldOptional<OrderTerm> | null | undefined) {
  return getModificationType(field, getEnumDelta)
}

export function getHaircutTypeModificationType(field: NegotiableFieldOptional<HaircutType> | null | undefined) {
  return isPricedByBothEntities(field) ? getModificationType(field, getEnumDelta) : null
}

/* Axe and Trade Conditions */

function getAxeTradeConditionDelta<T extends Pick<NegotiationDetail, 'negotiation'>>(
  data: T | null | undefined,
  compareField: CompareField = 'original'
) {
  const axeNegotiationValue = data && getNegotiatedValue(data.negotiation.isAxed)
  const axeOriginalValue = data && data.negotiation.isAxed[compareField]
  const tradeConditionNegotiationValue = data && getNegotiatedValue(data.negotiation.tradeConditionFlag)
  const tradeConditionOriginalValue = data && data.negotiation.tradeConditionFlag[compareField]
  return axeNegotiationValue !== axeOriginalValue || tradeConditionNegotiationValue !== tradeConditionOriginalValue
}

export function getAxeTradeConditionModificationType<T extends Pick<NegotiationDetail, 'negotiation'>>(data: T | null | undefined) {
  return getModificationType(data?.negotiation.isAxed, (_field: any, compareField?: CompareField) => {
    return getAxeTradeConditionDelta(data, compareField)
  })
}

/* RFQ Type */

export function getRFQTypeDelta(field: NegotiableFieldRequired<RfqType> | null | undefined, compareField: CompareField = 'original') {
  const negotiationValue = field && getNegotiatedValue(field)
  const originalValue = field && field[compareField]
  return negotiationValue !== originalValue
}

export function getRFQTypeModificationType(field: NegotiableFieldRequired<RfqType> | null | undefined) {
  return getModificationType(field, getRFQTypeDelta)
}

/* Boolean */

export function getBooleanDelta(field: NegotiableFieldOptional<boolean> | null | undefined, compareField: CompareField = 'original') {
  const negotiationValue = field && getNegotiatedValue(field)
  const originalValue = field && field[compareField]
  return negotiationValue !== originalValue
}

export function getBooleanModificationType(field: NegotiableFieldOptional<boolean> | null | undefined) {
  return getModificationType(field, getBooleanDelta)
}
